/* The AI Workspace as a figure. It starts the way the real one does: an empty workspace with one question and one
   composer in the middle. Anne's e-mail is pasted in, the start button is pressed – and the «Studio Bergen» case runs
   from inquiry to sent proposal on the real step rail, chat and canvas sheets (the same components as the app).
   Scripted, but live: the rail, the chat, quantities and discounts all work – totals use the same rules as the prototype. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowUpRight, LayoutPanelLeft, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtNOK, fmtPct } from "@/lib/format";
import { StaticAuthProvider } from "@/context/AuthContext";
import { CompanyLogo, UserAvatar } from "@/components/common";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { StepRail } from "@/components/workspace/StepRail";
import { Chat } from "@/components/workspace/Chat";
import { UnderstandPanel, DesignPanel, CalculatePanel, ProposePanel, SendPanel, EmptyCanvas } from "@/components/workspace/Canvas";
import { WorkspaceStart } from "@/pages/WorkspaceHomePage";
import { Inert, Stage } from "./Stage";
import { ACCOUNT, OPPORTUNITY, CONTACTS, REQUIREMENTS, DEMO_USER, OWNER, COMPANY, EMAIL_DRAFT, makeSolution, makeQuote, withTotals } from "./mockData";

/* ------------------------------------------------------------------ script */

export const INQUIRY =
  "Hi David,\n\nWe're planning a compact news studio in Bergen and need a proposal. Four remotely controlled 4K cameras run by one operator, a production core that fits our ST 2110 roadmap (but keep SDI tie-lines to existing kit), wireless audio for anchors and guests, redundant recording and SRT contribution to our distribution, monitoring in control and LED lighting for two zones.\n\nInstallation must happen in weeks 27–31. Budget is around 2–2.5 MNOK. Can you include training for journalists and technical staff?\n\nAnne Hansen, Technical Manager, NRK";

const uid = () => `m_${Math.random().toString(36).slice(2, 9)}`;
const msg = (role, content, extra = {}) => ({ id: uid(), role, content, created_at: new Date().toISOString(), ...extra });

const replyUnderstand = () =>
  msg("assistant", "I've read Anne's e-mail and set up the opportunity.\n\n**7 requirements** extracted across video, audio, control, streaming, lighting and infrastructure – 5 must-haves. **2 open questions** before I design: which distribution endpoint the SRT stream goes to, and whether the control room already has a Dante network.\n\nBudget (2–2.5 MNOK) and the installation window (weeks 27–31) are saved on the opportunity.", {
    actions_done: [{ label: "Opportunity ‘Studio Bergen’ created for NRK" }, { label: "7 requirements added" }, { label: "2 open questions" }, { label: "Anne Hansen added as contact" }],
    quick_replies: ["Design the solution", "Make sensible assumptions", "What would you ask Anne?"],
  });

const replyDesign = (t) =>
  msg("assistant", `Here's a first design, built only from your catalogue.\n\n**Camera system** – 4 × Panasonic AW-UE160 on ceiling mounts: 4K, NDI and ST 2110, which fits NRK's IP roadmap.\n**Production** – ATEM 2 M/E Constellation 4K with a Smart Videohub 40x40 for SDI tie-lines, two HyperDecks and a Ki Pro Ultra for redundant recording, two HELO Plus for SRT contribution.\n**Audio** – Yamaha QL1 with Sennheiser EW-DX wireless. **Lighting** – 6 × Nova P300c and 2 × LS 600d for two zones.\n\nAssumed: SRT to NRK national distribution, no existing Dante network. Total **${fmtNOK(t.total_sales)} NOK**, gross margin **${fmtPct(t.gm_pct)}**.`, {
    actions_done: [{ label: "Solution ‘Recommended’ created" }, { label: `${t.item_count} lines from the catalogue` }, { label: "2 assumptions noted" }],
    quick_replies: ["Continue to calculation", "Give 5 % on cameras", "Remove the Ki Pro"],
  });

const replyCalculate = (t) =>
  msg("assistant", `Totals are deterministic – recalculated from catalogue prices, not estimated.\n\nTotal **${fmtNOK(t.total_sales)} NOK** excl. VAT, cost ${fmtNOK(t.total_cost)}, gross margin **${fmtPct(t.gm_pct)}**. Hardware is ${Math.round((t.hardware / t.total_sales) * 100)} % of the deal, services ${Math.round((t.services / t.total_sales) * 100)} %.\n\n${t.approval.required ? `**${t.approval.label}** – ${t.approval.reason}.` : "No discounts yet, so this is **within your mandate** – no approval needed."} Longest lead time is ${t.max_lead_time_weeks} weeks (AW-UE160), which fits the week 27 start if we order by week 21.`, {
    quick_replies: ["Write the proposal", "Give 5 % on cameras", "Show margin per group"],
  });

const replyPropose = () =>
  msg("assistant", "Proposal v1 is ready for Anne – summary, our understanding, the solution, investment, timeline and terms.\n\nI kept the executive summary short and led with the installation window, since that was her main concern. Totals come straight from the calculation; assumptions and exclusions are listed so nothing is implied.", {
    actions_done: [{ label: "Quote v1 drafted" }, { label: "Valid 30 days" }],
    quick_replies: ["Send proposal", "Shorten the executive summary", "Warmer, more personal tone"],
  });

const replySend = () =>
  msg("assistant", "Sent. Anne gets a link where she can read, download and accept online – views and responses land on the opportunity, and when she accepts, the order is ready for Visma Business.\n\nHere's a cover e-mail you can paste:", {
    actions_done: [{ label: "Proposal v1 sent to anne.hansen@nrk.no" }, { label: "Cover e-mail drafted" }],
    email_draft: EMAIL_DRAFT,
    quick_replies: ["Draft a follow-up for next week", "Go to the opportunity"],
  });

const replyDiscount = (t, pct, group) =>
  msg("assistant", `Done – ${pct} % on ${group}. New total **${fmtNOK(t.total_sales)} NOK**, gross margin **${fmtPct(t.gm_pct)}**. ${t.approval.required ? `That needs **${t.approval.label.toLowerCase()}** (${t.approval.reason.toLowerCase()}).` : "Still **within your mandate**."}`, {
    actions_done: [{ label: `${pct} % discount on ${group}` }, { label: "Totals recalculated" }],
    quick_replies: ["Write the proposal", "Remove the discount", "Show margin per group"],
  });

const replyRemove = (t, name) =>
  msg("assistant", `Removed ${name}. Total is now **${fmtNOK(t.total_sales)} NOK** at **${fmtPct(t.gm_pct)}** gross margin. Recording is still redundant with the two HyperDecks.`, {
    actions_done: [{ label: `${name} removed` }, { label: "Totals recalculated" }],
    quick_replies: ["Continue to calculation", "Write the proposal"],
  });

const replyGroups = (sol) => {
  const rows = sol.groups
    .map((g) => {
      const sales = g.items.reduce((a, it) => a + (it.sales_price || 0) * (1 - (it.discount_pct || 0) / 100) * (it.qty || 0), 0);
      const cost = g.items.reduce((a, it) => a + (it.cost_price || 0) * (it.qty || 0), 0);
      return `- **${g.name}** – ${fmtNOK(sales)} NOK · GM ${fmtPct(sales ? ((sales - cost) / sales) * 100 : 0)}`;
    })
    .join("\n");
  return msg("assistant", `Margin per group:\n\n${rows}\n\nServices carry the margin; cameras are the thinnest line. If you need room to negotiate, that's where a project price from Panasonic would help.`, { quick_replies: ["Write the proposal", "Give 5 % on cameras"] });
};

const replyQuestion = () =>
  msg("assistant", "Good one to put to Anne. I've drafted a short e-mail asking about it – until she answers I'll assume SRT to NRK national distribution and a new Dante network, both flagged as assumptions in the proposal.", { quick_replies: ["Design the solution", "Make sensible assumptions"] });

const replyGeneric = (step) =>
  msg("assistant", "In the live system I'd answer that from the opportunity, your catalogue and your pricing rules. This preview runs a fixed case – try one of the suggestions, or move through the steps above.", {
    quick_replies: { understand: ["Design the solution"], design: ["Continue to calculation", "Give 5 % on cameras"], calculate: ["Write the proposal", "Show margin per group"], propose: ["Send proposal"], send: ["Go to the opportunity"] }[step] || [],
  });

const initial = () => ({ step: "understand", busy: false, beat: -1, messages: [], ctx: { opportunity: null, account: null, contacts: [], requirements: [], solution: null, quote: null, owner: OWNER, company: COMPANY }, emailDraft: null });

const BEATS = [
  { step: "understand", user: INQUIRY, busy: 2600, apply: (ctx) => ({ ...ctx, opportunity: OPPORTUNITY, account: ACCOUNT, contacts: CONTACTS, requirements: REQUIREMENTS }), reply: () => replyUnderstand() },
  { step: "design", user: "Design the solution – make sensible assumptions on the open questions.", busy: 3000, apply: (ctx) => ({ ...ctx, solution: withTotals(makeSolution()) }), reply: (ctx) => replyDesign(ctx.solution.totals) },
  { step: "calculate", user: null, busy: 1400, apply: (ctx) => ctx, reply: (ctx) => replyCalculate(ctx.solution.totals) },
  { step: "propose", user: "Write the proposal.", busy: 2800, apply: (ctx) => ({ ...ctx, quote: makeQuote(ctx.solution, "draft") }), reply: () => replyPropose() },
  { step: "send", user: "Send it to Anne.", busy: 1600, apply: (ctx) => ({ ...ctx, quote: makeQuote(ctx.solution, "sent") }), reply: () => replySend(), email: true },
];

/* the state after beat `at` has landed (0 … 4) – for still frames of the workspace */
export const stateAt = (at) => {
  const s = initial();
  let ctx = s.ctx;
  const messages = [];
  let emailDraft = null;
  let step = "understand";
  for (let i = 0; i <= at && i < BEATS.length; i++) {
    const b = BEATS[i];
    if (b.user) messages.push(msg("user", b.user));
    ctx = b.apply(ctx);
    messages.push(b.reply(ctx));
    if (b.email) emailDraft = EMAIL_DRAFT;
    step = b.step;
  }
  return { step, busy: false, beat: at, messages, ctx, emailDraft };
};

const reachedStep = (ctx) => {
  const q = ctx?.quote;
  if (q && q.status !== "draft") return "send";
  if (q) return "propose";
  if (ctx?.solution && (ctx.solution.groups || []).some((g) => (g.items || []).length)) return "calculate";
  return "understand";
};

/* ------------------------------------------------------------------ the demo */

const useDemo = (active, { seed = null, onReset } = {}) => {
  const [st, setSt] = useState(initial);
  const [tick, setTick] = useState(0);
  const manualUntil = useRef(0);
  const timer = useRef(null);
  const stRef = useRef(st);
  stRef.current = st;

  useEffect(() => () => clearTimeout(timer.current), []);

  const touch = useCallback(() => {
    manualUntil.current = Date.now() + 22000;
    setTick((x) => x + 1);
  }, []);

  const runBeat = useCallback((i, userText) => {
    const b = BEATS[i];
    if (!b) return;
    clearTimeout(timer.current);
    const text = userText === undefined ? b.user : userText;
    setSt((s) => ({ ...s, step: b.step, busy: true, messages: text ? [...s.messages, msg("user", text)] : s.messages }));
    timer.current = setTimeout(() => {
      setSt((s) => {
        const ctx = b.apply(s.ctx);
        return { ...s, busy: false, beat: Math.max(s.beat, i), ctx, emailDraft: b.email ? EMAIL_DRAFT : s.emailDraft, messages: [...s.messages, b.reply(ctx)] };
      });
    }, b.busy);
  }, []);

  /* a free-form or quick reply: do the thing it asks for, deterministically */
  const say = useCallback(
    (text, { manual = true } = {}) => {
      if (manual) touch();
      const s = stRef.current;
      if (s.busy) return;
      const t = text.toLowerCase();
      const has = (re) => re.test(t);
      if (has(/design the solution|sensible assumptions|example inquiry/)) return runBeat(s.ctx.opportunity ? 1 : 0, s.ctx.opportunity ? text : INQUIRY);
      if (has(/continue to calculation|calculation/) && s.ctx.solution) return runBeat(2, text);
      if (has(/write the proposal|proposal/) && s.ctx.solution && !has(/send/)) return runBeat(3, text);
      if (has(/send/) && s.ctx.quote) return runBeat(4, text);
      clearTimeout(timer.current);
      setSt((x) => ({ ...x, busy: true, messages: [...x.messages, msg("user", text)] }));
      timer.current = setTimeout(() => {
        setSt((x) => {
          let ctx = x.ctx;
          let reply;
          if (has(/5 ?% on cameras|discount on cameras/) && ctx.solution) {
            ctx = { ...ctx, solution: withTotals({ ...ctx.solution, groups: ctx.solution.groups.map((g) => (g.name === "Camera system" ? { ...g, items: g.items.map((it) => ({ ...it, discount_pct: 5 })) } : g)) }) };
            reply = replyDiscount(ctx.solution.totals, 5, "cameras");
          } else if (has(/remove the discount/) && ctx.solution) {
            ctx = { ...ctx, solution: withTotals({ ...ctx.solution, groups: ctx.solution.groups.map((g) => ({ ...g, items: g.items.map((it) => ({ ...it, discount_pct: 0 })) })) }) };
            reply = replyDiscount(ctx.solution.totals, 0, "all lines");
          } else if (has(/remove the ki pro|remove ki pro/) && ctx.solution) {
            ctx = { ...ctx, solution: withTotals({ ...ctx.solution, groups: ctx.solution.groups.map((g) => ({ ...g, items: g.items.filter((it) => it.product_id !== "p_aja_kipro_ultra") })) }) };
            reply = replyRemove(ctx.solution.totals, "the AJA Ki Pro Ultra");
          } else if (has(/margin per group/) && ctx.solution) {
            reply = replyGroups(ctx.solution);
          } else if (has(/about "|ask anne|what would you ask/)) {
            reply = replyQuestion();
          } else {
            reply = replyGeneric(x.step);
          }
          return { ...x, busy: false, ctx, messages: [...x.messages, reply] };
        });
      }, 1500);
      return undefined;
    },
    [runBeat, touch]
  );

  /* canvas actions – the same contract as the real page */
  const act = useCallback(
    (type, params = {}) => {
      touch();
      const s = stRef.current;
      if (s.busy) return;
      if (type === "go_to_step") return setSt((x) => ({ ...x, step: params.step }));
      if (type === "generate_solution") return runBeat(1, null);
      if (type === "generate_quote") return runBeat(3, null);
      if (type === "send_quote") return runBeat(4, null);
      if (type === "regenerate_solution") {
        clearTimeout(timer.current);
        setSt((x) => ({ ...x, busy: true }));
        timer.current = setTimeout(() => setSt((x) => ({ ...x, busy: false, messages: [...x.messages, msg("assistant", "I re-ran the design. Same result – the requirements haven't changed. Tell me what to change (swap a product, add a zone, cut cost) and I'll do it.", { quick_replies: ["Continue to calculation", "Remove the Ki Pro"] })] })), 1600);
        return undefined;
      }
      if (type === "update_item" || type === "remove_item" || type === "set_group_discount") {
        setSt((x) => {
          if (!x.ctx.solution) return x;
          const groups = x.ctx.solution.groups.map((g) => {
            if (type === "set_group_discount" && g.name === params.group_name) return { ...g, items: g.items.map((it) => (it.is_service ? it : { ...it, discount_pct: params.discount_pct })) };
            if (type === "update_item") return { ...g, items: g.items.map((it) => (it.id === params.item_id ? { ...it, qty: Math.max(1, params.qty) } : it)) };
            if (type === "remove_item") return { ...g, items: g.items.filter((it) => it.id !== params.item_id) };
            return g;
          });
          const solution = withTotals({ ...x.ctx.solution, groups });
          const quote = x.ctx.quote ? { ...x.ctx.quote, totals: solution.totals, items_snapshot: groups } : null;
          return { ...x, ctx: { ...x.ctx, solution, quote } };
        });
      }
      return undefined;
    },
    [runBeat, touch]
  );

  /* autoplay: the pasted e-mail first (right away), then one beat after another while in view and the viewer isn't
     interacting. When the story is told, hand back to the empty workspace. */
  useEffect(() => {
    if (!active || st.busy) return undefined;
    const next = st.beat + 1;
    const base = st.beat < 0 ? (seed ? 320 : 1400) : next < BEATS.length ? 5600 : 10000;
    const wait = Math.max(0, manualUntil.current - Date.now());
    const t = setTimeout(() => {
      if (manualUntil.current > Date.now()) return setTick((x) => x + 1);
      if (next < BEATS.length) runBeat(next, next === 0 && seed ? seed : undefined);
      else if (onReset) onReset();
      else setSt(initial());
      return undefined;
    }, base + wait);
    return () => clearTimeout(t);
  }, [active, st.busy, st.beat, tick, runBeat, seed, onReset]);

  return { st, say, act };
};

/* ------------------------------------------------------------------ the surface (same layout as WorkspacePage) */

const stopLinks = (e) => {
  const a = e.target.closest?.("a");
  if (a) e.preventDefault();
};

const WorkspaceSurface = ({ st, say, act, mode }) => {
  const { step, busy, ctx, messages } = st;
  const wide = mode === "full";
  const nar = mode === "narrow";
  const [view, setView] = useState("chat");
  const reached = useMemo(() => reachedStep(ctx), [ctx]);
  const goTo = (s) => s !== step && act("go_to_step", { step: s });
  const askInChat = (q) => say(`About "${q}" – `);

  // below the two-column layout, follow the story: canvas when the sheet changes, chat when a reply lands
  useEffect(() => {
    if (wide) return;
    setView(busy ? "chat" : ctx.opportunity ? "canvas" : "chat");
  }, [wide, busy, step, ctx.opportunity]);

  const canvas = !ctx.opportunity ? (
    <EmptyCanvas onSay={say} />
  ) : step === "understand" ? (
    <UnderstandPanel ctx={ctx} act={act} busy={busy} onAsk={askInChat} />
  ) : step === "design" ? (
    <DesignPanel ctx={ctx} act={act} busy={busy} />
  ) : step === "calculate" ? (
    <CalculatePanel ctx={ctx} act={act} busy={busy} />
  ) : step === "propose" ? (
    <ProposePanel ctx={ctx} act={act} busy={busy} onSay={say} />
  ) : (
    <SendPanel ctx={ctx} act={act} busy={busy} session={{ email_draft: st.emailDraft }} />
  );

  return (
    <div className="mb flex h-full w-full flex-col overflow-hidden bg-background text-foreground" data-testid="mock-workspace" data-step={step} data-beat={st.beat} data-busy={busy ? "1" : "0"} onClickCapture={stopLinks}>
      <header className={cn("grid h-14 shrink-0 items-center gap-3 px-3 sm:px-4", nar ? "grid-cols-[minmax(0,1fr)_auto]" : "grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]")} data-testid="mock-workspace-header">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground" aria-hidden="true">
            <ArrowLeft className="h-4 w-4" />
          </span>
          {ctx.opportunity ? (
            <div className="flex min-w-0 items-center gap-2.5">
              <CompanyLogo name={ctx.account?.name} domain={ctx.account?.website} size="sm" className="h-7 w-7 rounded-[8px] p-1" />
              <div className="min-w-0 truncate text-[13.5px]" data-testid="mock-workspace-title">
                <span className="font-semibold">{ctx.account?.name}</span>
                <span className="mx-1.5 text-muted-foreground/50">/</span>
                <span className="text-foreground/80">{ctx.opportunity.name}</span>
              </div>
            </div>
          ) : (
            <div className="text-[13.5px] font-semibold" data-testid="mock-workspace-title">New workspace</div>
          )}
        </div>
        {!nar && (
          <div className="flex justify-center">
            <StepRail step={step} reached={reached} dense={!wide} onSelect={goTo} />
          </div>
        )}
        <div className="flex items-center justify-end gap-1.5">
          {!wide && (
            <div className="inline-flex rounded-full bg-black/[0.045] p-1" role="tablist" aria-label="View">
              {[
                ["chat", "Chat", MessageSquare],
                ["canvas", "Canvas", LayoutPanelLeft],
              ].map(([k, label, Icon]) => (
                <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)} className={cn("flex h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-medium text-muted-foreground transition-[color,background-color,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-ring/40", view === k && "bg-white text-foreground shadow-[0_1px_2px_hsl(30_12%_18%/0.08),0_0_0_1px_hsl(30_12%_18%/0.05)]")} data-testid={`mock-workspace-view-${k}`}>
                  <Icon className="h-3.5 w-3.5" />
                  {!nar && <span>{label}</span>}
                </button>
              ))}
            </div>
          )}
          {ctx.opportunity && wide ? (
            <span className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-[12.5px] font-medium text-muted-foreground">
              Opportunity <ArrowUpRight className="h-3.5 w-3.5" />
            </span>
          ) : null}
          <UserAvatar name={DEMO_USER.name} initials={DEMO_USER.initials} color={DEMO_USER.color} src={DEMO_USER.avatar_url} size="md" className="ml-1 h-8 w-8" />
        </div>
      </header>
      {nar && (
        <div className="flex shrink-0 justify-center px-3 pb-2">
          <StepRail step={step} reached={reached} dense onSelect={goTo} />
        </div>
      )}

      <div className="grid min-h-0 flex-1" style={{ gridTemplateColumns: wide ? "420px minmax(0,1fr)" : "minmax(0,1fr)" }}>
        {wide || view === "chat" ? (
          <section className="flex min-h-0 min-w-0 flex-col" data-testid="mock-workspace-chat-pane" aria-label="Chat">
            <Chat messages={messages} step={step} busy={busy} onSend={say} className="h-full" placeholder={!ctx.opportunity ? "Paste an inquiry, e-mail or meeting notes…" : undefined} />
          </section>
        ) : null}
        {wide || view === "canvas" ? (
          <section className={cn("min-h-0 min-w-0 p-2 pt-0", wide && "p-3 pl-0 pt-0")} data-testid="mock-workspace-canvas" aria-label="Canvas">
            <div className="mx-auto h-full w-full max-w-[1320px]">
              <div key={step + (ctx.opportunity ? "o" : "") + (ctx.solution ? "s" : "") + (ctx.quote?.status || "")} className="sheet-enter h-full">
                {canvas}
              </div>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
};

const Workspace = ({ mode, active, seed, onReset }) => {
  const { st, say, act } = useDemo(active, { seed, onReset });
  return <WorkspaceSurface st={st} say={say} act={act} mode={mode} />;
};

/* ------------------------------------------------------------------ the start: an empty workspace, one question, one composer */

/* the e-mail lands in the composer in a few quick chunks – like a paste, not a typist */
const CHUNKS = (() => {
  const words = INQUIRY.match(/\S+\s*/g) || [INQUIRY];
  const n = 16;
  const per = Math.ceil(words.length / n);
  return Array.from({ length: n }, (_, i) => words.slice(0, (i + 1) * per).join("")).filter((t, i, a) => i === 0 || t !== a[i - 1]);
})();

const useStartScript = (active, onStart) => {
  const [text, setText] = useState("");
  const [focus, setFocus] = useState(false);
  const [pressing, setPressing] = useState(false);
  const manual = useRef(false);
  const timers = useRef([]);

  const clear = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  useEffect(() => {
    if (!active || manual.current) return undefined;
    setText("");
    setFocus(false);
    setPressing(false);
    const at = (ms, fn) => timers.current.push(setTimeout(fn, ms));
    at(1100, () => setFocus(true));
    CHUNKS.forEach((t, i) => at(1700 + i * 95, () => setText(t)));
    const end = 1700 + CHUNKS.length * 95;
    at(end + 650, () => setPressing(true));
    at(end + 850, () => onStart(INQUIRY));
    return clear;
  }, [active, onStart]);

  /* the viewer takes over: their text, their start */
  const onChange = (t) => {
    manual.current = true;
    clear();
    setFocus(true);
    setText(t);
  };
  const start = (t) => {
    clear();
    setPressing(true);
    timers.current.push(setTimeout(() => onStart(t), 180));
  };
  return { text, focus, pressing, onChange, start };
};

/* the sidebar and top bar of the app around an empty workspace (the real /workspace page sits inside the shell) */
const HomeShell = ({ mode, children }) => (
  <div className="mb flex h-full w-full overflow-hidden bg-background text-foreground" data-testid="mock-workspace-home" data-mode={mode}>
    {mode === "full" && (
      <Inert className="h-full shrink-0">
        <Sidebar activePath="/workspace" className="h-full" />
      </Inert>
    )}
    <div className="relative flex min-w-0 flex-1 flex-col">
      <Inert>
        <Topbar user={DEMO_USER} />
      </Inert>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  </div>
);

/* the question and the composer, centred – `text`, `focus`, `pressing` come from the script or the viewer */
export const StartSurface = ({ text, focus = false, pressing = false, onChange = () => {}, onStart = () => {}, onExample = () => {} }) => (
  <div className="flex h-full min-h-0 flex-col items-center justify-center px-5 sm:px-8" data-testid="mock-workspace-start" data-focus={focus ? "1" : "0"} data-pressing={pressing ? "1" : "0"} onClickCapture={stopLinks}>
    <WorkspaceStart first="David" text={text} onChange={onChange} onStart={onStart} onExample={onExample} busy={false} active={focus} pressing={pressing} testIdPrefix="mock-workspace-home" />
  </div>
);

const Start = ({ mode, active, onStart }) => {
  const { text, focus, pressing, onChange, start } = useStartScript(active, onStart);
  return (
    <HomeShell mode={mode}>
      <StartSurface text={text} focus={focus} pressing={pressing} onChange={onChange} onStart={start} onExample={(ex) => start(ex.text === "Use example inquiry" ? INQUIRY : ex.text)} />
    </HomeShell>
  );
};

/* ------------------------------------------------------------------ a still frame – for the system figure's AI Workspace tab */

export const WorkspaceStill = ({ at, mode }) => {
  const st = useMemo(() => (at < 0 ? null : stateAt(at)), [at]);
  if (!st) return <StartSurface text={INQUIRY} focus />;
  return <WorkspaceSurface st={st} say={() => {}} act={() => {}} mode={mode} />;
};

/* ------------------------------------------------------------------ the figure */

const URL_HOME = "os.mediability.no/workspace";
const URL_WORK = "os.mediability.no/workspace/studio-bergen";

export const WorkspaceMockup = ({ d }) => {
  const [phase, setPhase] = useState("start");
  const [seed, setSeed] = useState(null);
  const onStart = useCallback((t) => {
    setSeed(t || INQUIRY);
    setPhase("work");
  }, []);
  const onReset = useCallback(() => {
    setSeed(null);
    setPhase("start");
  }, []);
  return (
    <Stage url={phase === "start" ? URL_HOME : URL_WORK} caption={d?.ui?.prototype} height={820} narrowHeight={680} widths={{ fullWidth: 1440 }} testId="tilbod-workspace-mockup" attrs={{ "data-phase": phase }}>
      {({ mode, active }) => (
        <StaticAuthProvider user={DEMO_USER}>
          <div key={phase} className="mb-view-enter h-full">
            {phase === "start" ? <Start mode={mode} active={active} onStart={onStart} /> : <Workspace mode={mode} active={active} seed={seed} onReset={onReset} />}
          </div>
        </StaticAuthProvider>
      )}
    </Stage>
  );
};
